import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import GlassmorphFormField from '../ui/GlassmorphFormField'
import GlassmorphButton from '../ui/GlassmorphButton'

/**
 * AuthenticatedContactForm - Simplified contact form for logged-in users
 * Shows a streamlined message input since user is already identified
 * 
 * @param {string} newMessage - Current message text
 * @param {boolean} sending - Whether message is being sent
 * @param {Function} onMessageChange - Callback when message text changes
 * @param {Function} onSubmit - Callback to submit form
 */
const AuthenticatedContactForm = memo(function AuthenticatedContactForm({
  newMessage,
  sending,
  onMessageChange,
  onSubmit
}) {
  const { t } = useTranslation()

  return (
    <div className="py-6 px-6">
      <div className="max-w-md mx-auto">
        <h3 className="text-lg font-bold text-gray-900 mb-2">{t('channels.contactForm.title')}</h3>
        <p className="text-sm text-gray-600 mb-4">
          {t('channels.contactForm.subtitle')}
        </p>
        <p className="text-xs text-primary bg-primary-50/80 backdrop-blur-sm p-3 rounded-xl mb-4 border border-primary-100/50 shadow-sm">
          {t('channels.contactForm.signedInNotice')}
        </p>
        <form onSubmit={onSubmit} className="space-y-4">
          <GlassmorphFormField
            label={t('channels.contactForm.messageLabel')}
            name="message"
            type="textarea"
            value={newMessage}
            onChange={(e) => onMessageChange(e.target.value)}
            placeholder={t('channels.contactForm.messagePlaceholder')}
            rows={6}
            disabled={sending}
            helpText={newMessage.trim().length > 0 && newMessage.trim().length < 10 
              ? t('channels.message.charactersRemaining', { count: 10 - newMessage.trim().length })
              : null
            }
          />
          <GlassmorphButton
            variant="primary"
            onClick={(e) => {
              e.preventDefault()
              if (!newMessage.trim() || newMessage.trim().length < 10 || sending) return
              e.target.closest('form')?.requestSubmit()
            }}
            disabled={!newMessage.trim() || newMessage.trim().length < 10 || sending}
            className="w-full px-4 py-2 font-medium"
          >
            {sending ? t('channels.contactForm.sendingButton') : t('channels.contactForm.sendButton')}
          </GlassmorphButton>
        </form>
      </div>
    </div>
  )
})

export default AuthenticatedContactForm
